import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { Button } from '@nextui-org/react';
import { uploadReportImage } from 'common/src/api/reports';
import { toast } from 'react-toastify';
import { useRef } from 'react';

interface ReportEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
}

const ReportEditor = ({
  content,
  onChange,
  placeholder = 'Start writing your report...',
}: ReportEditorProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg',
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-brand-700 underline cursor-pointer',
        },
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class:
          'prose prose-sm sm:prose lg:prose-lg xl:prose-xl max-w-none focus:outline-none min-h-[400px] px-4 py-3',
      },
    },
  });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    try {
      toast.info('Uploading image...');
      const url = await uploadReportImage(file);

      if (url && editor) {
        editor.chain().focus().setImage({ src: url }).run();
        toast.success('Image uploaded successfully');
      } else {
        toast.error('Failed to upload image');
      }
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Failed to upload image');
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url && editor) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const removeLink = () => {
    if (editor) {
      editor.chain().focus().unsetLink().run();
    }
  };

  if (!editor) {
    return null;
  }

  return (
    <div className="border border-divider rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="bg-default-100 border-b border-divider p-2 flex flex-wrap gap-1">
        {/* Text Formatting */}
        <Button
          size="sm"
          variant={editor.isActive('bold') ? 'solid' : 'flat'}
          color={editor.isActive('bold') ? 'primary' : 'default'}
          onPress={() => editor.chain().focus().toggleBold().run()}
          className="min-w-[40px]"
        >
          <strong>B</strong>
        </Button>

        <Button
          size="sm"
          variant={editor.isActive('italic') ? 'solid' : 'flat'}
          color={editor.isActive('italic') ? 'primary' : 'default'}
          onPress={() => editor.chain().focus().toggleItalic().run()}
          className="min-w-[40px]"
        >
          <em>I</em>
        </Button>

        <Button
          size="sm"
          variant={editor.isActive('strike') ? 'solid' : 'flat'}
          color={editor.isActive('strike') ? 'primary' : 'default'}
          onPress={() => editor.chain().focus().toggleStrike().run()}
          className="min-w-[40px]"
        >
          <s>S</s>
        </Button>

        <div className="w-px h-6 bg-default-300 mx-1" />

        {/* Headings */}
        <Button
          size="sm"
          variant={editor.isActive('heading', { level: 1 }) ? 'solid' : 'flat'}
          color={
            editor.isActive('heading', { level: 1 }) ? 'primary' : 'default'
          }
          onPress={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
          className="min-w-[40px]"
        >
          H1
        </Button>

        <Button
          size="sm"
          variant={editor.isActive('heading', { level: 2 }) ? 'solid' : 'flat'}
          color={
            editor.isActive('heading', { level: 2 }) ? 'primary' : 'default'
          }
          onPress={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          className="min-w-[40px]"
        >
          H2
        </Button>

        <Button
          size="sm"
          variant={editor.isActive('heading', { level: 3 }) ? 'solid' : 'flat'}
          color={
            editor.isActive('heading', { level: 3 }) ? 'primary' : 'default'
          }
          onPress={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
          className="min-w-[40px]"
        >
          H3
        </Button>

        <div className="w-px h-6 bg-default-300 mx-1" />

        {/* Lists */}
        <Button
          size="sm"
          variant={editor.isActive('bulletList') ? 'solid' : 'flat'}
          color={editor.isActive('bulletList') ? 'primary' : 'default'}
          onPress={() => editor.chain().focus().toggleBulletList().run()}
          className="min-w-[40px]"
        >
          •
        </Button>

        <Button
          size="sm"
          variant={editor.isActive('orderedList') ? 'solid' : 'flat'}
          color={editor.isActive('orderedList') ? 'primary' : 'default'}
          onPress={() => editor.chain().focus().toggleOrderedList().run()}
          className="min-w-[40px]"
        >
          1.
        </Button>

        <Button
          size="sm"
          variant={editor.isActive('blockquote') ? 'solid' : 'flat'}
          color={editor.isActive('blockquote') ? 'primary' : 'default'}
          onPress={() => editor.chain().focus().toggleBlockquote().run()}
          className="min-w-[40px]"
        >
          "
        </Button>

        <div className="w-px h-6 bg-default-300 mx-1" />

        {/* Link */}
        <Button
          size="sm"
          variant={editor.isActive('link') ? 'solid' : 'flat'}
          color={editor.isActive('link') ? 'primary' : 'default'}
          onPress={addLink}
          className="min-w-[40px]"
        >
          🔗
        </Button>

        {editor.isActive('link') && (
          <Button
            size="sm"
            variant="flat"
            color="danger"
            onPress={removeLink}
            className="min-w-[40px]"
          >
            🔗✕
          </Button>
        )}

        <div className="w-px h-6 bg-default-300 mx-1" />

        {/* Image */}
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
        />
        <Button
          size="sm"
          variant="flat"
          color="default"
          onPress={() => fileInputRef.current?.click()}
          className="min-w-[40px]"
        >
          📷
        </Button>

        <div className="w-px h-6 bg-default-300 mx-1" />

        {/* Horizontal Rule */}
        <Button
          size="sm"
          variant="flat"
          color="default"
          onPress={() => editor.chain().focus().setHorizontalRule().run()}
          className="min-w-[40px]"
        >
          ―
        </Button>

        <div className="w-px h-6 bg-default-300 mx-1" />

        {/* Undo/Redo */}
        <Button
          size="sm"
          variant="flat"
          color="default"
          onPress={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          className="min-w-[40px]"
        >
          ↶
        </Button>

        <Button
          size="sm"
          variant="flat"
          color="default"
          onPress={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          className="min-w-[40px]"
        >
          ↷
        </Button>
      </div>

      {/* Editor Content */}
      <div className="bg-background">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default ReportEditor;
